"use client";

import { useEffect, useRef, type ReactNode } from "react";
import {
  flushGTMImpressions,
  getGTMBrandName,
  getGTMBusinessVertical,
  queueGTMImpression,
  sendGTMProductClick,
  type GTMImpression,
} from "@/lib/analytics/gtm";
import type { GTMProductDetail } from "@/lib/analytics/gtm/types";

/** Impressions waiting for a full batch are pushed after this much quiet time. */
const FLUSH_DELAY_MS = 1000;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush() {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushGTMImpressions();
  }, FLUSH_DELAY_MS);
}

/**
 * GTM tracking of one product tile in a slider / product list: the
 * impression once the tile is at least half visible (batched like the PLP
 * grid), and `productClick` when one of its links is followed.
 */
export function MagentoProductTileTracker({
  product,
  index,
  list,
  children,
}: {
  product: GTMProductDetail;
  /** 0-based position in the list. */
  index: number;
  list: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const withBrand = (): GTMProductDetail => ({ ...product, brand: product.brand || getGTMBrandName() });

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        const { quantity: _q, variant: _v, dimension1: _d, ...impression } = withBrand();
        void _q;
        void _v;
        void _d;
        // Slider impressions carry the business vertical, as the native shop's widget lists.
        const vertical = getGTMBusinessVertical();
        queueGTMImpression(
          { ...impression, list, position: index + 1, ...(vertical ? { google_business_vertical: vertical } : {}) } as GTMImpression,
          { batchSize: 4 }
        );
        scheduleFlush();
      },
      { threshold: 0.5 }
    );
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one impression per mounted tile
  }, []);

  return (
    <div
      ref={ref}
      className="h-full"
      onClickCapture={(event) => {
        if ((event.target as HTMLElement).closest("a")) {
          sendGTMProductClick({ product: withBrand(), index, list });
        }
      }}
    >
      {children}
    </div>
  );
}
